/** @jsxImportSource theme-ui */
/* eslint react/jsx-key: 0 */
import React from 'react'
import { Box, Button } from 'theme-ui'
import {
  PluginHook,
  useTable,
  UseTableOptions,
  usePagination,
  UsePaginationOptions,
} from 'react-table'
import {
  HiOutlineArrowNarrowLeft,
  HiOutlineArrowNarrowRight,
} from 'react-icons/hi'
export * from 'react-table'

interface Options extends UseTableOptions<{}>, UsePaginationOptions<{}> {}

export interface TableProps {
  options: Options
  plugins?: PluginHook<{}>[]
  totalData?: number
  onChangePage?: () => {}
  disablePagination?: boolean
  keyExtractor?: (item: any) => string
}

export const Table: React.FC<TableProps> = ({
  options,
  plugins,
  totalData,
  disablePagination,
  keyExtractor,
}) => {
  const tableInstance = useTable(options, usePagination, ...(plugins || []))

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    prepareRow,
  } = tableInstance

  const { pageIndex /* pageSize */ } = state
  // const pages = totalData / pageSize

  return (
    <Box sx={{ overflowX: ['scroll', null] }}>
      <table {...getTableProps()} sx={{ variant: 'styles.table' }}>
        <thead sx={{ variant: 'styles.thead' }}>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => {
                return (
                  <th
                    {...column.getHeaderProps()}
                    sx={{
                      variant: 'styles.th',
                      width: column.width,
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                      whiteSpace: ['nowrap', null],
                    }}
                  >
                    {column.render('Header')}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row)
            return (
              <tr
                {...row.getRowProps()}
                key={
                  keyExtractor?.(row.original) ||
                  row.original.id ||
                  row.getRowProps().id ||
                  row.id
                }
                sx={{ variant: 'styles.tr' }}
              >
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} sx={{ variant: 'styles.td' }}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>

      {!disablePagination && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <span sx={{ alignSelf: 'center', mr: '3' }}>
            Page{' '}
            <strong>
              {pageIndex + 1} of {pageOptions.length}
            </strong>
          </span>
          <Button
            variant="outline"
            sx={{ mr: '2', borderRadius: 'xl' }}
            onClick={previousPage}
            disabled={!canPreviousPage}
          >
            <HiOutlineArrowNarrowLeft
              sx={{ verticalAlign: 'middle', mr: '2' }}
            />
            Previous
          </Button>
          <Button
            variant="outline"
            sx={{ borderRadius: 'xl' }}
            onClick={nextPage}
            disabled={!canNextPage}
          >
            Next
            <HiOutlineArrowNarrowRight
              sx={{ verticalAlign: 'middle', ml: '2' }}
            />
          </Button>
        </Box>
      )}
    </Box>
  )
}

Table.defaultProps = {
  plugins: [],
}
